import { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';

import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type { CategoryEntity } from '@actual-app/core/types/models';

import { useImportTransactionsMutation } from '#accounts';
import { AyudaGavi } from '#components/mobile/ayuda/AyudaGavi';
import { OjoBuey } from '#components/mobile/ayuda/OjoBuey';
import { usePreguntasImportar } from '#components/mobile/ayuda/usePreguntasImportar';
import {
  buscarGruposMismoComercio,
  colocarMismoComercio,
} from '#components/mobile/comercios/buscarMismoComercio';
import {
  AvisoColocados,
  MismoComercio,
} from '#components/mobile/comercios/MismoComercio';
import type { GrupoComercio } from '#components/mobile/comercios/mismoComercioLogica';
import {
  aplicarHistoriaCuenta,
  desordenarHistoria,
  gruposPorComercio,
  ordenarHistoria,
  primerMesConRumbo,
} from '#components/mobile/historia/escribirHistoria';
import type { GrupoHistoria } from '#components/mobile/historia/escribirHistoria';
import { leerHistoria } from '#components/mobile/historia/historia';
import type { DatosHistoria } from '#components/mobile/historia/historia';
import { useColoresCategorias } from '#components/mobile/informes/useColoresCategorias';
import { MobileBackButton } from '#components/mobile/MobileBackButton';
import { comprobarPlan } from '#components/mobile/plan/cerrojoPlan';
import { leerTravesia } from '#components/mobile/travesia/estado';
import { useAtrasDePaso } from '#components/mobile/ui/atras';
import { Cargando } from '#components/mobile/ui/Cargando';
import { MobilePageHeader, Page } from '#components/Page';
import { useAccounts } from '#hooks/useAccounts';
import { useCategories } from '#hooks/useCategories';
import { useNavigate } from '#hooks/useNavigate';
import { usePayeesById } from '#hooks/usePayees';
import { useSheetValue } from '#hooks/useSheetValue';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch, useStore } from '#redux';
import { accountBalance, hastaHoy } from '#spreadsheet/bindings';
import { undo } from '#undo';
import { registrarUso } from '#uso';

import { lecturaConGuardada, leerFormatoGuardado } from './columnas';
import { DudasRepetidos } from './DudasRepetidos';
import type { Respuesta } from './DudasRepetidos';
import {
  aImportar,
  EXTENSIONES,
  filasVista,
  nombreArchivo,
  resumen,
  seleccionImportar,
  seTrae,
} from './importar';
import type { FilaVista } from './importar';
import { leerArchivo } from './leerArchivo';
import type { ErrorLectura } from './leerArchivo';
import { movimientosDeFilas } from './leerExtracto';
import type { Lectura, MovimientoLeido } from './leerExtracto';
import { buscarParecidos, fusionConApuntado } from './parecidos';
import type { Parecido } from './parecidos';
import { PasoColumnas } from './PasoColumnas';
import { PasoComprobar } from './PasoComprobar';
import { PasoElegir } from './PasoElegir';
import { PasoHecho } from './PasoHecho';
import { PasoVista } from './PasoVista';
import { pedirVistaPrevia } from './vistaPrevia';

type Paso =
  | { paso: 'elegir'; error: ErrorLectura | null }
  | { paso: 'cargando' }
  | { paso: 'comprobar' }
  | { paso: 'columnas' }
  | {
      paso: 'vista';
      filas: FilaVista[];
      fechaSaldoInicial: string | null;
      inicioHistoria: string | null;
      /** «¿Ya lo tenías?»: parecidos a uno apuntado a mano. */
      parecidos: Parecido[];
    }
  | {
      paso: 'hecho';
      nuevos: number;
      sinCategoria: number;
      historia: number;
      /** «Mismo comercio»: lo que se ofrece, o null si ya se contestó. */
      grupos: GrupoComercio[] | null;
      /** Cuántos se colocaron con «mismo comercio» (0 si no o deshecho). */
      colocados: number;
      /** Apuntados a mano sustituidos por el del banco («Es el mismo»). */
      sustituidos: number;
      /**
       * Con historia: los comercios los reconoce Gavi por su nombre y lo
       * colocado se deshace devolviéndolo a lo sin ordenar.
       */
      conHistoria: DatosHistoria | null;
      elegidos: GrupoHistoria[];
    };

/** Lo leído del archivo: filas de CSV/Excel o movimientos ya interpretados. */
type Archivo = {
  nombre: string;
  /** Solo CSV y Excel. */
  filas: string[][] | null;
  lectura: Lectura | null;
  movimientos: MovimientoLeido[];
};

/**
 * «Importar extracto» (`/accounts/:id/importar`): trae los movimientos de un
 * extracto del banco (CSV, Excel, Norma 43, OFX, QIF o CAMT) a la cuenta, en
 * cuatro pasos: elegir el archivo, comprobar cómo se ha leído, ver qué es
 * nuevo y qué ya estaba, y el resumen. Lo importado entra sin aprobar y lo
 * que no tiene categoría espera en «Por revisar», como lo del banco.
 */
export function ImportarExtractoPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const { data: comercios = {} } = usePayeesById();
  const { id = '' } = useParams();
  const location = useLocation();
  const { data: cuentas = [] } = useAccounts();
  const cuenta = cuentas.find(a => a.id === id);
  const { data: { list: categorias } = { list: [] as CategoryEntity[] } } =
    useCategories();
  const { huecoDe } = useColoresCategorias();
  const [formatoGuardado, setFormatoGuardado] = useSyncedPref(
    `importar-columnas-${id}`,
  );
  const [historiaRaw, setHistoriaRaw] = useSyncedPref('historia');
  const saldo =
    useSheetValue<'account', 'balance'>(hastaHoy(accountBalance(id))) ?? 0;
  const importar = useImportTransactionsMutation();

  const [paso, setPaso] = useState<Paso>({ paso: 'elegir', error: null });
  const [archivo, setArchivo] = useState<Archivo | null>(null);
  const [fuera, setFuera] = useState<Set<string>>(new Set());
  const [respuestas, setRespuestas] = useState<Map<string, Respuesta>>(
    new Map(),
  );
  const mismos = new Set(
    [...respuestas].filter(([, r]) => r === 'mismo').map(([fila]) => fila),
  );
  /** «Traer la historia»: encendido de entrada, como la maqueta. */
  const [conHistoria, setConHistoria] = useState(true);
  const [colocando, setColocando] = useState(false);
  // Ayuda de Gavi: explica el momento que se ve; si cambia (atrás del
  // móvil, un archivo compartido que acaba de cargar), se cierra.
  const [ayuda, setAyuda] = useState<Paso['paso'] | null>(null);
  if (ayuda && ayuda !== paso.paso) {
    setAyuda(null);
  }
  const preguntasAyuda = usePreguntasImportar(
    paso.paso === 'cargando' ? 'elegir' : paso.paso,
  );
  // «Atrás» retrocede un paso (vista → comprobar → elegir) en vez de salir
  // y perder el archivo; mientras se lee, no hace nada.
  useAtrasDePaso(
    paso.paso === 'elegir' || paso.paso === 'hecho' ? null : paso.paso,
    () => {
      if (paso.paso === 'columnas' || paso.paso === 'vista') {
        setPaso({ paso: 'comprobar' });
      } else if (paso.paso === 'comprobar') {
        setPaso({ paso: 'elegir', error: null });
      }
    },
  );

  const nombreCategoria = useMemo(
    () => new Map(categorias.map(c => [c.id, c.name])),
    [categorias],
  );

  const avisarError = (error: ErrorLectura) =>
    setPaso({ paso: 'elegir', error });

  const cargar = async (ruta: string) => {
    setPaso({ paso: 'cargando' });
    const leido = await leerArchivo(ruta);
    if (leido.tipo === 'error') {
      registrarUso('importar', { accion: 'error', error: leido.error });
      avisarError(leido.error);
      return;
    }
    if (leido.tipo === 'estructurado') {
      setArchivo({
        nombre: nombreArchivo(ruta),
        filas: null,
        lectura: null,
        movimientos: leido.movimientos,
      });
      setPaso({ paso: 'comprobar' });
      return;
    }
    // CSV o Excel: lo reconocido, o las columnas de la última vez si dan más.
    const { lectura, movimientos } = lecturaConGuardada(
      leido.filas,
      leido.lectura,
      leerFormatoGuardado(formatoGuardado),
    );
    setArchivo({
      nombre: nombreArchivo(ruta),
      filas: leido.filas,
      lectura,
      movimientos,
    });
    setPaso(
      lectura && movimientos.length > 0
        ? { paso: 'comprobar' }
        : { paso: 'columnas' },
    );
  };

  const elegir = async () => {
    const rutas = await window.Actual.openFileDialog({
      filters: [{ name: t('Bank statements'), extensions: EXTENSIONES }],
    });
    const ruta = rutas?.[0];
    if (ruta) {
      await cargar(ruta);
    }
  };

  // «Extracto en dos toques» sin Altamar (o sin columnas reconocidas): el
  // archivo compartido llega ya subido y se sigue paso a paso desde aquí.
  const rutaCompartida = (location.state as { rutaCompartida?: unknown })
    ?.rutaCompartida;
  const cargado = useRef(false);
  const cargarCompartida = useEffectEvent((ruta: string) => {
    void cargar(ruta);
  });
  useEffect(() => {
    if (typeof rutaCompartida === 'string' && !cargado.current) {
      cargado.current = true;
      cargarCompartida(rutaCompartida);
    }
  }, [rutaCompartida]);

  const verVistaPrevia = async (movimientos: MovimientoLeido[]) => {
    setPaso({ paso: 'cargando' });
    try {
      const vista = await pedirVistaPrevia(id, movimientos);
      if (!vista) {
        avisarError('ilegible');
        return;
      }
      const { fechaSaldoInicial } = vista;
      // Lo de antes del primer mes con Rumbo puede entrar como historia.
      const inicioHistoria =
        leerHistoria(historiaRaw)?.inicio ?? (await primerMesConRumbo());
      const filas = filasVista(
        movimientos,
        vista.entradas,
        fechaSaldoInicial,
        inicioHistoria,
      );
      setFuera(new Set());
      setRespuestas(new Map());
      setPaso({
        paso: 'vista',
        fechaSaldoInicial,
        inicioHistoria,
        filas,
        // Solo lo que entraría a los meses con Rumbo: la historia es de
        // antes, cuando no se apuntaba nada a mano.
        parecidos: buscarParecidos(
          filas.filter(f => seTrae(f) && !f.antes),
          vista.apuntados,
          id,
        ),
      });
    } catch {
      avisarError('ilegible');
    }
  };

  const traer = async (
    filas: FilaVista[],
    inicioHistoria: string | null,
    parecidos: Parecido[],
  ) => {
    if (!archivo || !comprobarPlan()) {
      return;
    }
    const { nuevos, conCategoria, historia } = resumen(
      filas,
      fuera,
      conHistoria,
    );
    // «Es el mismo»: entra el del banco con el nombre de lo apuntado en las
    // notas (y su categoría), y lo apuntado se borra después.
    const porFila = new Map(filas.map(f => [f.id, f]));
    const fusiones = new Map(
      parecidos.flatMap(p => {
        const fila = porFila.get(p.fila);
        return fila && mismos.has(p.fila) && !fuera.has(p.fila)
          ? [
              [
                p.fila,
                {
                  apuntado: p.apuntado.id,
                  ...fusionConApuntado(
                    p.apuntado,
                    (p.apuntado.payee && comercios[p.apuntado.payee]?.name) ||
                      '',
                    fila.categoria,
                  ),
                },
              ] as const,
            ]
          : [];
      }),
    );
    const traeHistoria = conHistoria && historia > 0 && inicioHistoria;
    const antes = store.getState().transactions.newTransactions.length;
    // Antes de importar: así «Deshacer» deshace la importación y no esto.
    if (archivo.filas && archivo.lectura) {
      setFormatoGuardado(JSON.stringify(archivo.lectura.columnas));
    }
    const seleccion = seleccionImportar(
      aImportar(id, archivo.movimientos).map(m => {
        const fusion = fusiones.get(m.trx_id);
        return fusion
          ? {
              ...m,
              ...(fusion.category ? { category: fusion.category } : null),
              ...(fusion.notes ? { notes: fusion.notes } : null),
            }
          : m;
      }),
      filas,
      fuera,
      !!traeHistoria,
    );
    let datosHistoria: DatosHistoria | null = null;
    try {
      if (traeHistoria) {
        // Saldo inicial hacia atrás, movimientos, y cada mes de antes de
        // Rumbo cuadrado.
        setPaso({ paso: 'cargando' });
        const datos = await aplicarHistoriaCuenta(send, t, {
          cuenta: id,
          inicio: inicioHistoria,
          historiaRaw,
          movimientos: seleccion,
        });
        setHistoriaRaw(JSON.stringify(datos));
        datosHistoria = datos;
      } else if (seleccion.length > 0) {
        await importar.mutateAsync({
          accountId: id,
          transactions: seleccion,
          reconcile: true,
          matchOnlySameBankLine: true,
        });
      }
      // Después de importar: «Deshacer» devuelve primero lo apuntado y
      // luego quita la importación.
      if (fusiones.size > 0) {
        await send('transactions-batch-update', {
          deleted: [...fusiones.values()].map(f => ({ id: f.apuntado })),
        });
      }
    } catch {
      if (traeHistoria) {
        avisarError('ilegible');
      }
      // La mutación ya avisa del error.
      return;
    }
    registrarUso('importar', {
      accion: 'importar',
      n: nuevos,
      ...(parecidos.length > 0
        ? { dudas: parecidos.length, mismos: fusiones.size }
        : null),
      ...(traeHistoria ? { historia } : null),
    });
    // Lo recién traído sin categoría de comercios que siempre van al mismo
    // sitio: Gavi pregunta una vez. Lo que ya estaba no se toca. Con
    // historia, Gavi reconoce los comercios por su nombre (Mercadona…).
    const cofres =
      leerTravesia(store.getState().prefs.synced.travesia)?.ids?.categorias ??
      {};
    const grupos: GrupoComercio[] = datosHistoria
      ? await gruposPorComercio(send, t, datosHistoria, cofres, id).catch(
          () => [],
        )
      : await buscarGruposMismoComercio(
          store.getState().transactions.newTransactions.slice(antes),
        ).catch(() => []);
    setPaso({
      paso: 'hecho',
      nuevos,
      sinCategoria: nuevos - conCategoria,
      historia: traeHistoria ? historia : 0,
      grupos: grupos.length > 0 ? grupos : null,
      colocados: 0,
      sustituidos: fusiones.size,
      conHistoria: datosHistoria,
      elegidos: [],
    });
  };

  const colocar = async (grupos: GrupoComercio[], payees: string[]) => {
    if (paso.paso !== 'hecho') {
      return;
    }
    setColocando(true);
    try {
      const elegidos = grupos.filter(g => payees.includes(g.payee));
      const historia = paso.conHistoria;
      const n = historia
        ? await ordenarHistoria(send, historia, elegidos as GrupoHistoria[])
        : await colocarMismoComercio(elegidos);
      registrarUso('importar', { accion: 'mismo-comercio', n });
      setPaso({
        ...paso,
        grupos: null,
        colocados: n,
        elegidos: historia ? (elegidos as GrupoHistoria[]) : [],
        // Lo de la historia no estaba en «sin categoría» de lo nuevo.
        sinCategoria: historia ? paso.sinCategoria : paso.sinCategoria - n,
      });
    } finally {
      setColocando(false);
    }
  };

  const deshacerColocados = async () => {
    if (paso.paso !== 'hecho') {
      return;
    }
    if (paso.conHistoria) {
      await desordenarHistoria(send, paso.conHistoria, paso.elegidos);
      setPaso({ ...paso, colocados: 0, elegidos: [] });
      return;
    }
    await send('undo');
    setPaso({
      ...paso,
      colocados: 0,
      sinCategoria: paso.sinCategoria + paso.colocados,
    });
  };

  const deshacer = async () => {
    // Si se colocaron por comercio, eso va encima de la importación.
    if (paso.paso === 'hecho' && paso.colocados > 0) {
      await send('undo');
    }
    if (paso.paso === 'hecho' && paso.sustituidos > 0) {
      await send('undo');
    }
    undo();
    dispatch(
      addNotification({
        notification: {
          type: 'message',
          message: t('Import undone'),
        },
      }),
    );
    void navigate(`/accounts/${id}`, { replace: true });
  };

  const titulo =
    paso.paso === 'vista' ? (cuenta?.name ?? '') : t('Import statement');

  let contenido;
  switch (paso.paso) {
    case 'elegir':
      contenido = (
        <PasoElegir
          nombreCuenta={cuenta?.name ?? ''}
          error={paso.error}
          onElegir={() => void elegir()}
        />
      );
      break;
    case 'cargando':
      contenido = <Cargando pantalla />;
      break;
    case 'comprobar':
      contenido = archivo && (
        <PasoComprobar
          archivo={archivo.nombre}
          movimientos={archivo.movimientos}
          onCambiarColumnas={
            archivo.filas ? () => setPaso({ paso: 'columnas' }) : undefined
          }
          onSeguir={() => void verVistaPrevia(archivo.movimientos)}
        />
      );
      break;
    case 'columnas':
      contenido = archivo?.filas && (
        <PasoColumnas
          filas={archivo.filas}
          lectura={archivo.lectura}
          onListo={lectura => {
            const filas = archivo.filas ?? [];
            setArchivo({
              ...archivo,
              lectura,
              movimientos: movimientosDeFilas(filas, lectura).movimientos,
            });
            setPaso({ paso: 'comprobar' });
          }}
        />
      );
      break;
    case 'vista':
      contenido = (
        <PasoVista
          filas={paso.filas}
          fechaSaldoInicial={paso.fechaSaldoInicial}
          fuera={fuera}
          onAlternar={idFila =>
            setFuera(f => {
              const r = new Set(f);
              if (!r.delete(idFila)) {
                r.add(idFila);
              }
              return r;
            })
          }
          nombreCategoria={c => nombreCategoria.get(c)}
          huecoCategoria={c => huecoDe(c)}
          importando={importar.isPending}
          onTraer={() =>
            void traer(paso.filas, paso.inicioHistoria, paso.parecidos)
          }
          inicioHistoria={paso.inicioHistoria}
          conHistoria={conHistoria}
          onHistoria={setConHistoria}
          mismos={mismos}
          pendientes={
            paso.parecidos.filter(p => !respuestas.has(p.fila)).length
          }
          dudas={
            paso.parecidos.length > 0 && (
              <DudasRepetidos
                parecidos={paso.parecidos}
                filas={paso.filas}
                respuestas={respuestas}
                onResponder={(fila, respuesta) =>
                  setRespuestas(r => new Map(r).set(fila, respuesta))
                }
                cuenta={id}
                nombreComercio={payee => comercios[payee]?.name}
                nombreCuenta={c => cuentas.find(a => a.id === c)?.name}
              />
            )
          }
        />
      );
      break;
    case 'hecho':
      contenido = (
        <PasoHecho
          nuevos={paso.nuevos}
          sinCategoria={paso.sinCategoria}
          historia={paso.historia}
          sustituidos={paso.sustituidos}
          nombreCuenta={cuenta?.name ?? ''}
          saldo={saldo}
          colocados={paso.colocados}
          mismoComercio={
            paso.grupos ? (
              <MismoComercio
                comercios={paso.grupos.map(g => ({
                  payee: g.payee,
                  nombre:
                    'nombre' in g && typeof g.nombre === 'string'
                      ? g.nombre
                      : (comercios[g.payee]?.name ?? ''),
                  nombreCategoria: nombreCategoria.get(g.categoria) ?? '',
                  n: g.ids.length,
                }))}
                colocando={colocando}
                onColocar={payees => void colocar(paso.grupos ?? [], payees)}
                onNo={() => setPaso({ ...paso, grupos: null })}
              />
            ) : paso.colocados > 0 ? (
              <AvisoColocados
                n={paso.colocados}
                onDeshacer={() => void deshacerColocados()}
              />
            ) : null
          }
          onRevisar={() => void navigate('/revisar', { replace: true })}
          onMasTarde={() => void navigate(`/accounts/${id}`, { replace: true })}
          // Con historia son varios cambios (saldo inicial, meses): no hay
          // un «Deshacer» de un toque.
          onDeshacer={paso.historia > 0 ? undefined : () => void deshacer()}
        />
      );
      break;
    default:
      contenido = null;
  }

  return (
    <Page
      header={
        <MobilePageHeader
          title={<span data-ayuda="titulo-importar">{titulo}</span>}
          leftContent={paso.paso === 'hecho' ? null : <MobileBackButton />}
          rightContent={
            paso.paso !== 'cargando' && (
              <OjoBuey onPress={() => setAyuda(paso.paso)} />
            )
          }
        />
      }
      padding={0}
    >
      <View style={{ flex: 1, minHeight: '100%' }} data-testid="importar">
        {contenido}
      </View>
      {ayuda && (
        <AyudaGavi
          pantalla={t('Import statement')}
          preguntas={preguntasAyuda}
          onCerrar={() => setAyuda(null)}
        />
      )}
    </Page>
  );
}
